import { Box } from "@mui/material";
import { ReadonlySignal, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import {
  AdvancedFilterModule,
  CellStyle,
  CellValueChangedEvent,
  ClientSideRowModelModule,
  ClipboardModule,
  ColDef,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnPivotChangedEvent,
  ColumnResizedEvent,
  ColumnRowGroupChangedEvent,
  ColumnsToolPanelModule,
  ColumnValueChangedEvent,
  ColumnVisibleEvent,
  CsvExportModule,
  CsvExportParams,
  DataTypeDefinition,
  ExcelExportModule,
  ExcelExportParams,
  ExpandOrCollapseAllEvent,
  FilterChangedEvent,
  FiltersToolPanelModule,
  GetMainMenuItemsParams,
  GridOptions,
  GridReadyEvent,
  IAggFuncParams,
  IGetRowsParams,
  LicenseManager,
  MasterDetailModule,
  MenuModule,
  ModuleRegistry,
  MultiFilterModule,
  PaginationChangedEvent,
  RangeSelectionModule,
  RichSelectModule,
  RowClickedEvent,
  RowGroupingModule,
  RowValueChangedEvent,
  SelectionChangedEvent,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
  SizeColumnsToFitGridStrategy,
  SortChangedEvent,
  SortDirection,
  SparklinesModule,
  StatusBarModule,
} from "ag-grid-charts-enterprise";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import {
  debounce,
  isArray,
  isBoolean,
  isEqual,
  isNil,
  isObject,
  isUndefined,
  max,
  min,
  toNumber,
  isString,
  isEmpty
} from "lodash-es";
import { ForwardedRef, forwardRef, RefObject, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button, LayoutItem, List, ListColumn, Maybe, SimpleContainer } from "../../../resolvers-types";
import NGLayoutItem from "../../generators/NGLayoutItem";
import {
  IButtonMethods,
  INGListProps,
  RowGroupPanelShowOptions,
  RowGroupDisplayTypeOptions,
  RuntimeContext,
} from "../../library/NGFieldExtensions";
import OdataProvider from "../../library/ODataProvider";
import { OdataQueryExtendFull } from "../../library/ODataProvider/types";
import {
  GetConvertedLabelsForListFromSiteSettings,
  GetFormatFromSite,
  getScope,
  getState,
  mapFields,
  setupHandlers,
  setupLocalState,
  updateItemContext,
} from "../../library/dataService";
import { fixGridMetadata, matchTypename } from "../../library/metadataUtils";
import { callOdataQuery, ODataQueryOptions } from "../../library/oDataUtils";
import { generateUID, getCustomLabel, getTestId, isNullOrEmpty, traverse } from "../../library/utils";
import NGButton from "../NGButton/NGButton";
import CellRendererList from "./NGCellRendererList";
import {
  addBlankRows,
  addRowPerPageToSelector,
  agGridLocale,
  checkboxSelection,
  CustomColDef,
  defaultExportParams,
  defaultGroupColumn,
  defaultRowHeight,
  excelStyles,
  fromColDefsToConfig,
  getCustomFilters,
  getDataTypeFilter,
  getFilterParams,
  getFormatDataType,
  getRowId,
  getRows,
  IAnyObject,
  removePageElements,
  resolveClasses,
  resolveColumnBindings,
} from "./NGListUtils";

import { getSessionSetting, removeSessionSetting, setSessionSetting } from "../../library/settingsManager";
import CustomHeaderCheckbox from "./CustomHeaderCheckbox";
import QueryRequest from "./oDataQueryBuilder";

import "ag-grid-charts-enterprise/styles/ag-grid.css"; // Mandatory CSS required by the Data Grid
import "ag-grid-charts-enterprise/styles/ag-theme-quartz.css"; // Mandatory CSS required by the Data Grid
import "./NGList.css";

LicenseManager.setLicenseKey(
  "Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-064288}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{JBI_Holdings}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{JBI}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{JBI}_need_to_be_licensed___{JBI}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{29_July_2025}____[v3]_[0102]_MTc1Mzc0MzYwMDAwMA==bd659fcc281dec57d192f14cfd09e0c3"
);

ModuleRegistry.registerModules([
  AdvancedFilterModule,
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  CsvExportModule,
  ClipboardModule,
  ColumnsToolPanelModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  MasterDetailModule,
  MenuModule,
  MultiFilterModule,
  RangeSelectionModule,
  RichSelectModule,
  RowGroupingModule,
  SetFilterModule,
  SideBarModule,
  StatusBarModule,
  SparklinesModule,
]);

type IPinned = "left" | "right";
type IOnColumnUpdate =
  | ColumnRowGroupChangedEvent
  | ColumnPivotChangedEvent
  | ColumnMovedEvent
  | ColumnPinnedEvent
  | ColumnVisibleEvent
  | ColumnValueChangedEvent
  | SortChangedEvent;

const NGList = forwardRef(({ config, context }: INGListProps, ref?: ForwardedRef<AgGridReact<IAnyObject>>) => {
  const AGGridRef = useRef<AgGridReact<IAnyObject>>(null);
  const GridRef = (ref || AGGridRef) as RefObject<AgGridReact<IAnyObject>>;
  const ContainerRef = useRef<HTMLDivElement>(null);
  const GridContainerRef = useRef<HTMLDivElement>(null);
  const AdvanceFilterRef = useRef<HTMLDivElement>(null);
  const QuickFilterRef = useRef<HTMLInputElement>(null);
  const testId = getTestId(config);

  const currentContext = updateItemContext(context, config, { GridRef, QuickFilterRef });

  const local = setupLocalState(
    config,
    {
      Columns: useSignal(config.Columns ?? []),
      ListColumns: useSignal(config.ListColumns ?? []),
      Rows: useSignal(config.Rows ?? []),
      Loading: useSignal(config.Loading ?? false),
      QuickFilterText: useSignal(config.QuickFilterText ?? undefined),
      Service: useSignal(config.Service ?? {}),
      Refresh: useSignal(config.Refresh ?? false),
      ClearGridState: useSignal(config.ClearGridState ?? false),
      RowsPerPage: useSignal(config.RowsPerPage ?? 20),
    },
    currentContext
  );
  const location = useLocation();

  function getColumns(
    cols: Maybe<ListColumn>[] | undefined | null,
    listCols: Maybe<ListColumn>[] | undefined | null,
    _context: RuntimeContext,
    config: List
  ): CustomColDef[] {
    const MUICols: CustomColDef[] = [];
    let allCols = [...(cols ?? []), ...(isArray(listCols) ? listCols : [])];

    if (isNil(allCols) || allCols.length === 0) return MUICols;

    // check settings, if exists, return the settings
    const colSessionSettings = getSessionSetting("settings-grid", config.Id);
    if (isArray(colSessionSettings)) {
      const colMap = colSessionSettings.reduce((acc, col, idx) => {
        acc[col.Name ?? col.Id] = { Index: idx, Column: col };
        return acc;
      }, {});

      allCols = allCols
        .sort((a, b) => {
          if (!a || !b) return 0;
          const aColMap = colMap[a.Name ?? a.Id];
          const bColMap = colMap[b.Name ?? b.Id];
          if (!aColMap || !bColMap) return 0;
          return aColMap.Index - bColMap.Index;
        })
        .map((c) => {
          if (!c) return c;
          const cColMap = colMap[c.Name ?? c.Id];
          if (!cColMap) return c;
          return {
            ...c,
            Sort: colMap[c.Name ?? c.Id].Column.Sort,
            Visible: colMap[c.Name ?? c.Id].Column.Visible,
          };
        });
    }

    allCols.forEach((col) => {
      if (!col) return;
      resolveColumnBindings(col, context);

      const format = col.FormatName ? GetFormatFromSite(col.FormatName) : null;
      const newCol: CustomColDef = {
        field: col.Name as string,
        valueFormatter: (p) => getFormatDataType(format, p.value, col.DataType),
        filter: col.DisableFiltering === true ? false : getDataTypeFilter(col.DataType),
        editable: col.Editable as boolean,
        sortable: col.DisableSorting === true ? false : true,
        sortingOrder: col.SortingOrder?.length ? (col.SortingOrder as SortDirection[]) : undefined,
        sort: col.Sort as SortDirection,
        hide: !isUndefined(col.Visible) ? !col.Visible : false,
        headerName: getCustomLabel(col.HeaderName as string),
        suppressHeaderMenuButton: !!col.SuppressHeaderMenu,
        cellStyle: col.Style as CellStyle,
        cellClass: resolveClasses(col.Classes, col.FormatName),
        colId: col.Name ?? col.Id,
        flex: col.Flex ?? undefined,
        cellDataType: col.DataType ?? undefined,
        rowGroup: col.RowGroup ?? undefined,
        minWidth: col.ColumnMinimumWidth ?? undefined,
        width: col.ColumnFixedWidth ?? undefined,
        resizable: col.Resizable ?? true,
        aggFunc: col.AggFunc,
        autoSize: col.AutoSize ?? false,
        defaultAggFunc: col.DataType === "number" ? "sum" : "first",
        pivot: col.Pivot ?? undefined,
        pinned: col.Pinned as IPinned,
        listCol: col,
        enableRowGroup: config.DisabledColumnGrouping ? col.DisableGrouping === false : !col.DisableGrouping,
        filterParams: getFilterParams(col.DataType, col.FilterParams),
      };

      // fix for old metadata
      fixGridMetadata(col);

      setCellRenderer(newCol, col);

      MUICols.push(newCol);
    });

    return MUICols;
  }

  function setCellRenderer(c: CustomColDef, col?: ListColumn | null) {
    if (isArray(col?.CellRendererItems) && col.CellRendererItems.length) {
      c.cellRenderer = (params: CustomCellRendererProps) => (
        <CellRendererList
          cellProps={params}
          context={currentContext}
          components={col.CellRendererItems}
          config={config}
        />
      );
    }
  }

  const handlers = setupHandlers(config, currentContext, location);

  function onInput() {
    GridRef.current?.api.setGridOption("quickFilterText", QuickFilterRef.current?.value);
    local.QuickFilterText.value = QuickFilterRef.current?.value;
  }

  function convertLegacyGrid() {
    return {
      __typename: "SimpleContainer",
      Id: generateUID(),
      IgnoreLayout: true,
      Style: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        minHeight: "20px",
        flex: 1,
        paddingTop: "0.5rem",
        gap: "0.5rem",
      },
      Items: [
        // add items from config.Toolbar.Items
        ...(config?.Toolbar?.Items ?? []),
        ...(config?.Items ?? []),
      ],
    } as SimpleContainer;
  }

  function EditToolbar() {
    const hasTopContainer = config.Items && config.Items[0] && config.Items[0]?.__typename == "SimpleContainer";

    // Toolbar at the top level always has a container
    const container = hasTopContainer ? (config.Items as any)[0] : convertLegacyGrid();

    if (!hasTopContainer) config.Items = [container];

    function addItem(item: any, position: number) {
      if (isNil(container.Items)) container.Items = [];

      if (position === -1) {
        container.Items.push(item);
      } else {
        container.Items.splice(position, 0, item);
      }
    }

    const quickFilterRefs: any[] = [];
    traverse(config.Items, 0, quickFilterRefs, "ListQuickFilter");

    const exportRefs: any[] = [];
    traverse(config.Items, 0, exportRefs, "ListExportButton");

    const clearAllRefs: any[] = [];
    traverse(config.Items, 0, clearAllRefs, "ListClearAllButton");

    const hasExportButton = exportRefs.length > 0;
    const hasQuickFilter = quickFilterRefs.length > 0;
    const hasClearAllRefs = clearAllRefs.length > 0;

    if (!hasExportButton)
      addItem(
        {
          Id: generateUID(),
          __typename: "ListExportButton",
          EnableCSV: true,
          EnableExcel: true,
          Visible: config.ShowExport !== false && config?.Toolbar?.ShowExport !== false,
        },
        0
      );

    if (!hasQuickFilter) {
      const quickFilterAction = config?.Actions?.find((a) => a?.Trigger === "onQuickFilterChange");
      if (quickFilterAction != null) {
        addItem(
          {
            Id: generateUID(),
            __typename: "ListQuickFilter",
            Actions: [quickFilterAction],
            Adornment: { Position: "Start", Icon: { IconName: "Search" } },
            Visible: config.ShowQuickFilter !== false,
            //onInput: onInput
          },
          -1
        );
      }
    }

    if (!hasClearAllRefs)
      addItem(
        {
          Id: generateUID(),
          __typename: "ListClearAllButton",
          Visible: config.ShowClearAllFilters !== false,
        },
        1
      );

    quickFilterRefs.forEach((ref) => {
      ref.onInput = onInput;
    });

    return (
      <>
        {config.Items?.map((item, key) => (
          <NGLayoutItem key={item?.Id ?? key} config={item as LayoutItem} context={currentContext} />
        ))}
      </>
    );
  }

  function BottomButtons() {
    if (!config?.SpreadsheetModeOptions?.ShowAddRowsButton) return null;

    const addRowsToSheetButton: Button = {
      __typename: "Button",
      Id: `${config.Id}_AddRowsButton`,
      Label: "Add 5 Rows",
      StartIcon: { IconName: "Add", Id: `${config.Id}_AddRowsButtonIcon` },
      Actions: [
        {
          Trigger: "onClick",
          preHandler: () => {
            const newRows = addBlankRows(5);
            local.Rows.value = [...local.Rows.value, ...newRows];
          },
        },
      ],
    };

    return (
      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
        <NGButton config={addRowsToSheetButton} methods={{} as IButtonMethods} context={currentContext} />
      </Box>
    );
  }

  function handleOnRowClicked(params: RowClickedEvent<IAnyObject>) {
    handlers["onRowClick"]?.(null, params.data);
  }

  function onSelectionChanged(params: SelectionChangedEvent<IAnyObject>) {
    const rows = params.api.getSelectedRows();
    // handle select all current page case
    const selectedNodes = params.api.getRenderedNodes().filter((n) => n.isSelected());

    const selectedRows = rows.length ? rows : selectedNodes.map((n) => n.data);

    handlers["onSelectionChanged"]?.(null, selectedRows);
  }

  const onRowValueChanged = useCallback(
    (params: RowValueChangedEvent) => {
      const clonedRows = [...local.Rows.value];
      clonedRows[params.rowIndex ?? 0] = params.data;
      handlers["onRowUpdate"]?.(null, { Rows: clonedRows });
    },
    [handlers, local.Rows.value]
  );

  const onCellValueChanged = useCallback(
    (params: CellValueChangedEvent) => {
      const clonedRows = [...local.Rows.value];
      clonedRows[params.rowIndex ?? 0] = params.data;
      if (!isNil(handlers["onCellUpdate"])) handlers["onCellUpdate"](null, { Rows: clonedRows });
    },
    [handlers, local.Rows.value]
  );

  const handleGridEditorEvent = useCallback(
    (setFn: () => List | undefined) => {
      const { parentState } = getState(currentContext);
      if (handlers["onGridConfigurationChange"] && matchTypename("List", parentState?.["SelectedComponent"]?.value)) {
        const Config = setFn();
        if (Config) {
          // remove grid configuration change event to avoid infinite loop
          Config.Actions = Config?.Actions?.filter((a) => a?.Trigger !== "onGridConfigurationChange");
          handlers["onGridConfigurationChange"](new Event("change"), { Config });
        }
      }
    },
    [handlers, currentContext]
  );

  const onColumnManuallyResized = useCallback(
    (params: ColumnResizedEvent<IAnyObject>) => {
      if (params.source === "uiColumnResized") {
        const colDef = params.column?.getColDef();
        const listColumns = [...local.ListColumns.value];
        const colIndex = listColumns.findIndex((c) => c.Id === colDef?.colId);
        if (colIndex !== -1) {
          listColumns[colIndex].ColumnFixedWidth = params.column?.getActualWidth();
        }

        handleGridEditorEvent(() => ({
          ...config,
          ListColumns: listColumns,
        }));
      }
    },
    [config, local.ListColumns.value, handleGridEditorEvent]
  );

  const onColumnsUpdate = useCallback(
    (event: IOnColumnUpdate) => {
      const columnDefs = event?.api?.getColumnDefs();
      if (!columnDefs) return;
      const settings = columnDefs.map((c: ColDef) => {
        return {
          Sort: c.sort,
          Id: c.colId,
          Visible: !c.hide,
        };
      });
      setSessionSetting("settings-grid", config.Id, settings);
      handleGridEditorEvent(() => {
        const columnsDefinitions = columnDefs;
        const ListColumns = fromColDefsToConfig(columnsDefinitions as CustomColDef[]);
        if (!isEqual(ListColumns, local.ListColumns.value)) {
          return { ...config, ListColumns };
        }
        return undefined;
      });
    },
    [config, local.ListColumns.value, handleGridEditorEvent]
  );

  const cols: ReadonlySignal<CustomColDef[]> = useComputed(() =>
    getColumns(local.Columns.value, local.ListColumns.value, currentContext, config)
  );

  const rowData: ReadonlySignal<IAnyObject[]> = useComputed(() => getRows(local.Rows.value));

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const gridOptions: GridOptions<IAnyObject> = useMemo(
    () => ({
      onColumnPivotModeChanged(event) {
        event.api.sizeColumnsToFit();
      },
      statusBar: {
        statusPanels: [
          { statusPanel: "agTotalRowCountComponent" },
          { statusPanel: "agFilteredRowCountComponent" },
          { statusPanel: "agSelectedRowCountComponent", align: "left" },
          { statusPanel: "agAggregationComponent", align: "right" },
        ],
      },
      rowDragManaged: true,
      rowDragMultiRow: true,
      popupParent: GridContainerRef.current,
      pivotPanelShow: "always",
      suppressColumnMoveAnimation: false,
      enableRtl: /[?&]rtl=true/.test(window.location.search),
      enableRangeSelection: true,
      enableFillHandle: true,
      suppressClearOnFillReduction: false,
      groupSelectsChildren: true,
      columnTypes: {
        currencyType: {
          useValueFormatterForExport: false,
        },
      },
      getBusinessKeyForNode: (node) => (node.data ? node.data.name : ""),
      initialGroupOrderComparator: ({ nodeA, nodeB }) => {
        if (!nodeA?.key || !nodeB?.key) return 0;
        return nodeA?.key < nodeB?.key ? -1 : nodeA?.key > nodeB?.key ? 1 : 0;
      },
      advancedFilterParent: ContainerRef.current,
      excelStyles: excelStyles(),
      localeText: GetConvertedLabelsForListFromSiteSettings(),
      dataTypeDefinitions: {
        date: {
          baseDataType: "date",
          extendsDataType: "date",

          valueParser: (params) => _parseDateTimeFromString(params.newValue == null ? null : String(params.newValue)),
          valueFormatter: (params) => {
            if (params.value == null) {
              return "";
            }
            if (!(params.value instanceof Date)) {
              if (dateRegex.test(params.value)) {
                return params.value;
              } else if (isNaN((params.value as Date).getTime())) return "Invalid Date";
            }
            return _serialiseDate(params.value, false) ?? "";
          },
          dataTypeMatcher: (value) => value instanceof Date || (typeof value === "string" && dateRegex.test(value)),
        } as DataTypeDefinition,
      },
    }),
    []
  );

  //----------------------------------
  // AG Grid Functions START
  //----------------------------------

  function _padStartWidthZeros(value, totalStringSize) {
    return value.toString().padStart(totalStringSize, "0");
  }

  function _serialiseDate(date, includeTime = true, separator = "-") {
    if (!date) {
      return null;
    }
    let serialised = [date.getFullYear(), date.getMonth() + 1, date.getDate()]
      .map((part) => _padStartWidthZeros(part, 2))
      .join(separator);
    if (includeTime) {
      serialised +=
        " " +
        [date.getHours(), date.getMinutes(), date.getSeconds()].map((part) => _padStartWidthZeros(part, 2)).join(":");
    }
    return serialised;
  }

  function _parseDateTimeFromString(value) {
    if (!value) {
      return null;
    }
    const [dateStr, timeStr] = value.split(" ");
    if (!dateStr) {
      return null;
    }
    const fields = dateStr.split("-").map((f) => parseInt(f, 10));
    if (fields.filter((f) => !isNaN(f)).length !== 3) {
      return null;
    }
    const [year, month, day] = fields;
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }
    if (!timeStr || timeStr === "00:00:00") {
      return date;
    }
    const [hours, minutes, seconds] = timeStr.split(":").map((part) => parseInt(part, 10));
    if (hours >= 0 && hours < 24) {
      date.setHours(hours);
    }
    if (minutes >= 0 && minutes < 60) {
      date.setMinutes(minutes);
    }
    if (seconds >= 0 && seconds < 60) {
      date.setSeconds(seconds);
    }
    return date;
  }

  //----------------------------------
  // AG Grid Functions END
  //----------------------------------

  const handleSidebarOpened = (e) => {
    if (e.source !== "sideBarButtonClicked" && e.visible && !config.SideBarOpenByDefault) {
      e.api.closeToolPanel();
    }
  };

  const onFirstDataRendered = useCallback(() => {
    const colDefs = GridRef.current?.api.getColumnDefs() ?? [];
    if (config.EnableSideBar) {
      for (const def of colDefs as unknown as CustomColDef[]) {
        if (def?.pivot) {
          GridRef.current?.api.setGridOption("pivotMode", true);
          break;
        }
      }
    }

    const autoSizeColumns = colDefs.filter((c: CustomColDef) => c?.autoSize && c?.colId);
    if (autoSizeColumns.length)
      GridRef.current?.api.autoSizeColumns(autoSizeColumns.map((c: CustomColDef) => c.colId!));

    GridRef.current?.api.closeToolPanel();
  }, [config, GridRef]);

  const onExpandOrCollapseAll = useCallback(
    (e: ExpandOrCollapseAllEvent) => {
      handleGridEditorEvent(() => ({
        ...config,
        GroupingsCollapsed: e.source === "collapseAll",
      }));
    },
    [config, handleGridEditorEvent]
  );

  const resolveServiceFields = useCallback(
    (request: IGetRowsParams): ODataQueryOptions => {
      const service = local.Service.value;
      if (service?.Fields) {
        const { state, form, parentState } = getState(currentContext);

        const scope = getScope(currentContext, config, state, form, {}, parentState);
        const [params, isValid] = mapFields(service.Fields, scope);
        if (isValid) {
          const { Filter, GroupBy, Aggregate, OrderBy: OrderByParam, OData } = params;
          const sortModel = request.sortModel;
          // filter out repeated sort columns
          const OrderBy = OrderByParam?.filter((o) => !sortModel.find((s) => o.includes(s.colId)));
          const queryRequest = new QueryRequest();
          queryRequest.Filter = Filter;
          queryRequest.GroupBy = GroupBy;
          queryRequest.Aggregate = Aggregate;
          queryRequest.OrderBy = OrderBy;
          queryRequest.OData = OData;
          const odataQuery = queryRequest.toODataQuery();
          return odataQuery as ODataQueryOptions;
        }
      }
      return {};
    },
    [config, currentContext, local.Service.value]
  );

  const getNextDay = (dateFrom: Date): Date => {
    const newDate = new Date(dateFrom); // Create a copy to avoid mutating the original
    newDate.setDate(newDate.getDate() + 1); // Add one day
    return newDate;
  };

  const getDateOnly = (d: string): string => {
    if (isNullOrEmpty(d)) return d;

    if (d.length > 10) {
      return d.substring(0, 10);
    }

    return d;
  };

  // Parse $orderby into an array of column:order pairs
  const parseOrderBy = (orderBy) => {
    if (!orderBy) return [];
    return orderBy.split(",").map((col) => {
      const [column, order] = col.trim().split(/\s+/);
      return { column, order: order?.toLowerCase() === "desc" ? "desc" : "asc" };
    });
  };

  // Create a map from options.sort
  const parseSortOptions = (sortOptions) => {
    return (sortOptions ?? []).map((col) => {
      const [column, order] = col.trim().split(/\s+/);
      return { column, order: order?.toLowerCase() === "desc" ? "desc" : "asc" };
    });
  };

  // Combine options.sort and $orderby
  const mergeOrderBy = useCallback((optionsSort, orderBy) => {
    const sortMap = new Map();

    // Add options.sort columns to the map (highest priority)
    parseSortOptions(optionsSort).forEach(({ column, order }) => {
      sortMap.set(column, order);
    });

    // Add $orderby columns if not already in the map
    parseOrderBy(orderBy).forEach(({ column, order }) => {
      if (!sortMap.has(column)) {
        sortMap.set(column, order);
      }
    });

    // Build the final result
    return Array.from(sortMap.entries())
      .map(([column, order]) => (order ? `${column} ${order}` : column))
      .join(",");
  },[]);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      if (document.documentElement.clientWidth <= 1024) {
        event.api.closeToolPanel();
      }
      const customFilters = getCustomFilters(cols.value);

      const provider = new OdataProvider({
        isCaseSensitiveStringFilter: true,
        callApi: async (options) => {
          const serviceConfig = { ...local.Service.value };
          return await callOdataQuery({ options, context: currentContext, serviceConfig, config });
        },
        beforeRequest(query, provider, request) {
          Object.entries(request.filterModel).forEach(([key, value]) => {
            if ((value as any).filterType !== "date") return;

            const datatype = local.ListColumns.value.find((c) => c.Id === key)?.DataType;

            const f = value as any;

            if (datatype !== "datetime") {
              if (!isNullOrEmpty(f.dateFrom)) f.dateFrom = getDateOnly(f.dateFrom);
              if (!isNullOrEmpty(f.dateTo)) f.dateTo = getDateOnly(f.dateTo);
              return;
            }
            // For datetime, we need to ensure the dateTo is set to the next day
            switch (f.type) {
              case "equals":
                f.dateTo = getNextDay(new Date(f.dateFrom));
                f.type = "inRange";
                break;
            }
          });

          const { $filter, $orderby } = resolveServiceFields(request as IGetRowsParams);
          provider.toDateTime = (value: string): string | null => {
            if (value?.length <= 10) return value;

            return new Date(value).toUTCWithoutTimezone();
          };
          provider.toQuery = (options: OdataQueryExtendFull): string => {
            const path: Record<string, string> = {};
            const orderBy = mergeOrderBy(options.sort, $orderby);
            const filter = [...(options.filter ?? []), $filter ?? ""].filter(Boolean).join(" and ");
            if (options.count) {
              path["$count"] = true + "";
            }
            if (options.skip) {
              path["$skip"] = options.skip + "";
            }
            if (options.top) {
              path["$top"] = options.top + "";
            }
            if (orderBy) {
              path["$orderby"] = orderBy;
            }
            if (filter) {
              path["$filter"] = filter;
            }
            if (options.apply && options.apply.length > 0) {
              path["$apply"] = options.apply.join("/");
            }
            if (options.expand && options.expand.length > 0) {
              path["$expand"] = options.expand.join(",");
            }
            if (options.select && options.select.length > 0) {
              path["$select"] = options.select.join(",");
            }

            let query = "";
            if (Object.keys(path).length > 0) {
              query = "?" + new URLSearchParams(path).toString();
            }
            return query;
          };
          query.select = cols.value.map((c) => c.field).filter((field) => field !== undefined);
        },
        afterLoadData(_options, rowData) {
          // when new page returns 0 rows, rowData.length === 0, need to take into acount all rows
          const rowsCount = GridRef.current?.api.getDisplayedRowCount();
          rowsCount ? event.api.hideOverlay() : event.api.showNoRowsOverlay();
          // In order to export data properly, verifying the rowData length
          GridRef.current?.api.setGridOption("rowData", rowData);
        },
        customFilters,
      });
      event.api.setGridOption("serverSideDatasource", provider);

      const filterSessionModel = getSessionSetting("settings-filter-grid", config.Id);
      if (filterSessionModel) event.api.setFilterModel(filterSessionModel);

      removePageElements(testId ?? "", [".ag-paging-row-summary-panel, .ag-paging-description"]);
    },
    [
      GridRef,
      cols.value,
      config,
      currentContext,
      local.ListColumns.value,
      local.Service.value,
      mergeOrderBy,
      resolveServiceFields,
      testId,
    ]
  );

  useSignalEffect(() => {
    if (isBoolean(local.Refresh.value) && local.Refresh.value) {
      GridRef.current?.api.refreshServerSide({ route: undefined, purge: false });
      local.Refresh.value = false;
    } else if (isObject(local.Refresh.value) && local.Refresh.value?.RefreshRows) {
      const { Purge = false, Route = undefined } = local.Refresh.value;
      GridRef.current?.api.refreshServerSide({ route: Route, purge: Purge });
      local.Refresh.value = { RefreshRows: false, Purge: false, Route };
    }
  });

  useSignalEffect(() => {
    if (local.ClearGridState.value) {
      GridRef.current?.api.setFilterModel(null);
      GridRef.current?.api.deselectAll();
      local.ClearGridState.value = false;
    }
  });

  // const rowsPerPage = useSignal(20); // 20 row per page by default

  const onPaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const newPageSize = event.api.paginationGetPageSize();

      // Only refresh if the page size actually changed
      if (newPageSize !== local.RowsPerPage.value) {
        local.RowsPerPage.value = newPageSize;

        // Refresh the server-side data with the new page size
        event.api.refreshServerSide({
          route: undefined,
          purge: true, // Use true to clear cache and force reload with new block size
        });
      }
    },
    [local.RowsPerPage]
  );

  const defaultHeaderAndPaginationHeight = 148;
  // if "autoHeight" is selected, gridHeight will be calculated based on the number of rows
  const gridHeight = useComputed(() => {
    const calculateHeight = config.GridLayout === "autoHeight" || !config.GridHeight;
    return calculateHeight
      ? `${(Number(config.RowHeight) || defaultRowHeight) * Number(local.RowsPerPage.value) + defaultHeaderAndPaginationHeight
        }px`
      : config.GridHeight;
  });

  const getMainMenuItems = (params: GetMainMenuItemsParams) => {
    const defaultItems = params.defaultItems;
    return defaultItems.map((item) => {
      if (item === "resetColumns") {
        return {
          name: "Reset Columns",
          action: () => {
            params.api.resetColumnState(); // Default reset action
            removeSessionSetting("settings-grid", config.Id);
            removeSessionSetting("settings-filter-grid", config.Id);
            params.api.setGridOption(
              "columnDefs",
              getColumns(local.Columns.value, local.ListColumns.value, currentContext, config)
            );
            params.api.setFilterModel(null);
          },
        };
      }
      return item;
    });
  };

  return (
    <div data-testid={testId} data-type={config.__typename} ref={GridContainerRef} style={{ width: "100%" }}>
      <EditToolbar />
      <div ref={AdvanceFilterRef} />
      <div
        className="ag-theme-quartz"
        style={{
          width: "100%",
          height: gridHeight.value ?? "auto",
        }}
      >
        <AgGridReact
          domLayout="normal"
          sideBar={
            config.EnableSideBar
              ? {
                  toolPanels: ["columns", "filters"],
                  position: "right",
                  defaultToolPanel: "",
                  hiddenByDefault: false,
                }
              : undefined
          }
          getMainMenuItems={getMainMenuItems}
          onToolPanelVisibleChanged={handleSidebarOpened}
          getChartToolbarItems={() => ["chartDownload"]}
          defaultColDef={{
            floatingFilter: !!config.ShowFloatingFilter,
            enableRowGroup: config.DisabledColumnGrouping !== true,
            editable: !!config.EnableEditing,
            filter: "agTextColumnFilter",
            flex: 1,
            enableValue: true,
            enablePivot: true,
            enableCellChangeFlash: true,
            cellClass: config.ColumnsClasses ?? undefined,
            headerClass: config.ColumnsHeaderClasses ?? undefined,
          }}
          rowSelection={
            config.ShowCheckboxSelection
              ? {
                  mode: (config.SelectionMode as "multiRow" | "singleRow") ?? "multiRow",
                  isRowSelectable: () => true,
                  checkboxes: (params) =>
                    config.ShowCheckboxSelection ? checkboxSelection(params, GridRef.current?.api) : false,
                  headerCheckbox: false,
                  hideDisabledCheckboxes: true,
                  selectAll: "currentPage",
                  enableClickSelection: config.EnableRowClickSelection ?? false,
                }
              : undefined
          }
          editType={config.EditMode === "fullRow" ? "fullRow" : undefined}
          getChildCount={(data) => data && data.childCount}
          cellSelection={!!config.EnableCellSelection}
          suppressCellFocus={!config.EnableCellSelection}
          pivotMode={!!config.PivotMode}
          ref={GridRef}
          rowData={rowData.value}
          columnDefs={cols.value}
          loading={local.Loading.value}
          getRowId={getRowId}
          autoSizeStrategy={
            config.RowsAutoSizeStrategy
              ? ({
                  type: config.RowsAutoSizeStrategy ?? "fitGridWidth",
                } as SizeColumnsToFitGridStrategy)
              : undefined
          }
          processUnpinnedColumns={(params) => {
            const columns = params.columns.map((col) => {
              const colDef = col.getColDef() as CustomColDef;
              if (colDef.listCol?.Pinned) {
                return {
                  ...col,
                  pinned: colDef.listCol.Pinned as IPinned,
                };
              }
              return col;
            });
            return columns;
          }}
          autoGroupColumnDef={defaultGroupColumn(config)}
          rowGroupPanelShow={(config.ShowGroupingPanel as RowGroupPanelShowOptions) ?? undefined}
          suppressAggFuncInHeader
          groupDefaultExpanded={config.GroupingsCollapsed ?? true ? 0 : -1}
          onRowClicked={handleOnRowClicked}
          onColumnValueChanged={onColumnsUpdate}
          onSelectionChanged={onSelectionChanged}
          onColumnResized={debounce(onColumnManuallyResized, 300)}
          onCellValueChanged={onCellValueChanged}
          rowHeight={config.RowHeight ?? defaultRowHeight}
          paginationPageSize={local.RowsPerPage.value}
          paginationAutoPageSize={config.PaginationAutoPageSize ?? undefined}
          paginationPageSizeSelector={config.ShowPageSizeSelector ? addRowPerPageToSelector(local.RowsPerPage.value) : false}
          onFilterChanged={(event: FilterChangedEvent) => {
            const filterModel = event.api.getFilterModel();
            Object.entries(filterModel).forEach(([, value]) => {
              if ((value as any).filterType !== "date") return;
              const f = value as any;
              // save settings with filter fixed (view beforeRequest method)
              if (f.type === "inRange" && f.dateTo instanceof Date && isString(f.dateFrom)) {
                f.dateTo = null;
                f.type = "equals";
              }
            });
            setSessionSetting("settings-filter-grid", config.Id, event.api.getFilterModel());
          }}
          selectionColumnDef={{
            headerComponent: CustomHeaderCheckbox,
            headerComponentParams: { showHeaderCheckbox: !!config.ShowCheckboxSelection },
          }}
          groupDisplayType={(config.RowGroupDisplayType as RowGroupDisplayTypeOptions) ?? undefined}
          localeText={agGridLocale}
          pagination={!!config.ShowPagination}
          gridOptions={gridOptions as GridOptions<unknown>}
          defaultCsvExportParams={defaultExportParams as CsvExportParams}
          defaultExcelExportParams={defaultExportParams as unknown as ExcelExportParams}
          enableAdvancedFilter={!!config.ShowAdvancedFilter}
          advancedFilterParent={AdvanceFilterRef.current}
          onFirstDataRendered={onFirstDataRendered}
          readOnlyEdit={!!config.ReadOnlyEdit}
          onRowValueChanged={onRowValueChanged}
          onExpandOrCollapseAll={onExpandOrCollapseAll}
          onSortChanged={onColumnsUpdate}
          onColumnPinned={onColumnsUpdate}
          onColumnMoved={(event) => {
            if (!event.finished || event.source === "gridOptionsChanged") return;
            onColumnsUpdate(event);
          }}
          onColumnPivotChanged={onColumnsUpdate}
          onColumnVisible={onColumnsUpdate}
          onPaginationChanged={onPaginationChanged}
          cacheBlockSize={local.RowsPerPage.value}
          maxBlocksInCache={5000}
          onGridReady={onGridReady}
          rowModelType={!isEmpty(local.Service.value) ? "serverSide" : "clientSide"}
          aggFuncs={{
            sum: (params: IAggFuncParams) => {
              let sum = 0;
              params.values.forEach((value) => {
                if (typeof value === "number" || params.colDef.cellDataType === "number") sum += toNumber(value);
              });
              return sum ?? undefined;
            },
            max: (params: IAggFuncParams) => {
              return max(params.values) ?? undefined;
            },
            min: (params: IAggFuncParams) => {
              return min(params.values) ?? undefined;
            },
          }}
        />
      </div>
      <BottomButtons />
    </div>
  );
});

export default NGList;
